/**
 * Sidebar Header Component
 *
 * @module shared/components/navigation/SidebarHeader
 *
 * Header section with hamburger toggle and logo
 */

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { memo } from 'react';
import { NotificationBell } from '@/modules/notification/presentation/components';

interface SidebarHeaderProps {
  collapsed: boolean;
  onToggle?: (() => void) | undefined;
}

export const SidebarHeader = memo(function SidebarHeader({ collapsed, onToggle }: SidebarHeaderProps) {
  const muiTheme = useMuiTheme();

  return (
    <Box
      sx={{
        p: collapsed ? 0.5 : 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexDirection: collapsed ? 'column' : 'row',
        justifyContent: collapsed ? 'center' : 'flex-start',
        minHeight: 64,
        flexShrink: 0,
      }}
    >
      {/* Hamburger Toggle Button */}
      <IconButton
        onClick={onToggle}
        size="small"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        sx={{
          bgcolor: 'background.default',
          borderRadius: 1,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <MenuIcon fontSize="small" />
      </IconButton>

      {/* Logo - Only visible when expanded */}
      {!collapsed && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            transition: muiTheme.transitions.create(['opacity', 'visibility'], {
              duration: muiTheme.transitions.duration.shorter,
            }),
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: `linear-gradient(135deg, ${muiTheme.palette.primary.main}, ${muiTheme.palette.secondary.main})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
              color: 'white',
              flexShrink: 0,
            }}
          >
            AC
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2} noWrap>
              AutoCollect
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                color: 'text.secondary',
              }}
            >
              v2.1
            </Typography>
          </Box>
        </Box>
      )}
      <Box sx={{ ml: collapsed ? 0 : 'auto' }}>
        <NotificationBell />
      </Box>
    </Box>
  );
});
